import React from 'react';
import {XMarkIcon} from '@heroicons/react/20/solid';
import {useHubspotContactPropertyDefinitionsQuery} from 'api';
import useCondition from '../../../useCondition';
import CheckboxValue from './CheckboxValue';
import {HubspotContactCheckboxSearchCondition} from './types';

interface Props {
  searchCondition: HubspotContactCheckboxSearchCondition;
  setSearchCondition: (
    searchCondition: HubspotContactCheckboxSearchCondition,
  ) => void;
  onDelete: () => void;
}

const PropertyField = ({
  searchCondition,
  setSearchCondition,
  onDelete,
}: Props) => {
  const {condition, poolId} = useCondition();

  const {data: {hubspotContactPropertyDefinitions = []} = {}} =
    useHubspotContactPropertyDefinitionsQuery({
      variables: {poolId},
      skip: !poolId,
    });

  // 検索用テーブルに展開しているのは単一チェックボックスのみ。
  // 同じプロパティを重複して条件に指定できないようにする。
  //
  // Salesforce 側は fieldName が __c で終わるカスタム項目だけに絞っているが、
  // HubSpot には名前の規約が無いため同じ絞り込みはできない。
  // 判定するなら hubspotDefined を見ることになるが、
  //   - hs_ が付かない標準項目がある（company_size / gender / date_of_birth 等）
  //   - カスタム項目に標準的な名前が使われている（category / department / url 等）
  //   - 値は true と null の 2 値で false は返らないため === false では拾えない
  // という事情があり、そもそも bool 型のカスタム項目自体が存在しないプールも多い。
  // 絞ると選択肢がゼロになるので、標準項目も含めて出す。
  // hs_email_optout のように標準項目でも実用上の意味があるものがある。
  const targetProperties = hubspotContactPropertyDefinitions
    .filter((property) => property.fieldType === 'booleancheckbox')
    .filter(
      (property) =>
        !condition.hubspotContactSearchConditions?.some(
          (cond: HubspotContactCheckboxSearchCondition) =>
            cond.hubspot_contact_property_definition_id === property.id &&
            searchCondition.hubspot_contact_property_definition_id !==
              property.id,
        ),
    );

  const onChangeProperty = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const property = targetProperties.find(
      (item) => item.id === e.target.value,
    );

    if (!property) {
      setSearchCondition({});
      return;
    }

    setSearchCondition({
      field_type: 'checkbox',
      hubspot_contact_property_definition_id: property.id,
      value: '',
    });
  };

  return (
    <div className="flex gap-2">
      <select
        className="form-select border-c-border rounded text-base w-64 h-[44px]"
        onChange={onChangeProperty}
        value={searchCondition.hubspot_contact_property_definition_id ?? ''}
      >
        <option value="" />
        {targetProperties.map((property) => (
          <option value={property.id} key={property.id}>
            {property.labelText || property.name}
          </option>
        ))}
      </select>
      {searchCondition.field_type === 'checkbox' && (
        <CheckboxValue
          property={targetProperties.find(
            (property) =>
              property.id ===
              searchCondition.hubspot_contact_property_definition_id,
          )}
          value={searchCondition.value}
          onChange={(value) => setSearchCondition({...searchCondition, value})}
        />
      )}
      <button
        onClick={onDelete}
        className="bg-white px-0 py-6 h-5 w-5 flex items-center justify-center rounded-full cursor-pointer"
      >
        <XMarkIcon className="w-5 h-5 text-c-light" />
      </button>
    </div>
  );
};

export default PropertyField;
